<template>
  <v-row>
    <v-dialog v-model="dialogActive" persistent width="600">
      <v-card>
        <v-card-title>{{
          $t("components.dialogs.transferBoatDialog.transferBoat")
        }}</v-card-title>
        <v-card-subtitle class="mb-0 pb-0">
          {{
            $t(
              "components.dialogs.transferBoatDialog.transferBoatOwnershipToUser"
            )
          }}
        </v-card-subtitle>
        <v-card-text>
          <v-form lazy-validation v-model="valid" ref="form" @submit.prevent="">
            <v-autocomplete
              ref="autocomplete"
              class="mt-2 mb-2"
              v-model="assigneeUsername"
              :items="assigneeOptions"
              item-value="username"
              item-text="username"
              :label="
                $t('components.dialogs.transferBoatDialog.enterUserEmail')
              "
              @input.native="loadAssignees(false)"
              @keyup.enter="handleAddUser"
              @blur="handleAddUser"
              :search-input.sync="assigneeSearch"
              persistent-hint
              color="primary"
              clearable
              autocomplete="off"
              :rules="usernameRule"
              :hint="$t('components.dialogs.transferBoatDialog.required')"
            >
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title
                      v-html="
                        $t(
                          'components.dialogs.transferBoatDialog.pressEnterToConfirmEmail'
                        )
                      "
                    >
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>

              <template v-slot:append-outer>
                <v-select
                  class="mt-0 pt-0"
                  color="primary"
                  v-model="inviteUserLanguage"
                  :items="userLanguageOptions"
                  item-value="id"
                  item-text="title"
                >
                </v-select>
              </template>
            </v-autocomplete>
            <v-checkbox
              color="primary"
              :label="
                $t(
                  'components.dialogs.transferBoatDialog.transferHistoryIncludingTripsAlertsAndFullServicingHistory'
                )
              "
              v-model="transferHistoricalData"
            ></v-checkbox>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="handleCancel()"
            data-cy="importServiceProtocolDialogCancelBtn"
            >{{ $t("components.dialogs.transferBoatDialog.cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            text
            @click="handleSave()"
            data-cy="importServiceProtocolDialogCancelBtn"
            >{{ $t("components.dialogs.transferBoatDialog.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>
<script>
import debounce from "@/plugins/debounce";
import api from "@/utils/api.js";
import snackbar from "@/utils/snackbar";
import cookie from "@/utils/cookie";
import validation from "@/utils/validation";

export default {
  props: ["dialog", "dealersUuid"],
  data: () => ({
    emailRule: [],
    usernameRule: [],
    valid: false,
    assigneeOptions: [],
    assigneeSearch: "",
    assigneeUsername: null,
    otherEmailAddress: null,
    transferHistoricalData: true,
    timeout: null,
    inviteUserLanguage: null,
    userLanguageOptions: [],
  }),

  created() {
    this.setInitialData();
    this.loadAssignees(true);
  },
  computed: {
    dialogActive() {
      return !!this.dialog.active;
    },
  },
  methods: {
    setInitialData() {
      this.emailRule = [
        (v) =>
          !!v ||
          this.$t("components.dialogs.transferBoatDialog.eMailIsRequired"),
        (v) =>
          /.+@.+\..+/.test(v) ||
          this.$t("components.dialogs.transferBoatDialog.eMailMustBeValid"),
      ];
      this.usernameRule = [
        (v) =>
          !!v ||
          this.$t("components.dialogs.transferBoatDialog.userIsRequired"),
      ];

      this.userLanguageOptions = [
        {
          id: "en",
          title: this.$t(
            "components.selectors.multilingualDocumentSelector.english"
          ),
        },
        {
          id: "de",
          title: this.$t(
            "components.selectors.multilingualDocumentSelector.german"
          ),
        },
        {
          id: "fr",
          title: this.$t(
            "components.selectors.multilingualDocumentSelector.french"
          ),
        },
        {
          id: "it",
          title: this.$t(
            "components.selectors.multilingualDocumentSelector.italian"
          ),
        },
        {
          id: "es",
          title: this.$t(
            "components.selectors.multilingualDocumentSelector.spanish"
          ),
        },
      ];

      this.handleUserLanguageSelection();
    },

    setPreferredUserInviteLanguage(preferredInviteUserLanguage) {
      let language = this.userLanguageOptions.find(
        (item) => item.id === preferredInviteUserLanguage
      );

      cookie.setCookie(
        "preferred-invite-user-language",
        language == null ? "en" : language.id,
        9999
      );

      return language == null ? "en" : language.id;
    },

    getPreferredUserInviteLanguage() {
      const preferredInviteUserLanguage = cookie.getCookie(
        "preferred-invite-user-language"
      );

      let language = this.userLanguageOptions.find(
        (item) => item.id === preferredInviteUserLanguage
      );

      if (language != null) {
        return language.id;
      }

      language = this.userLanguageOptions.find((item) => item.id === "en");
      return language.id;
    },
    handleUserLanguageSelection() {
      this.inviteUserLanguage = this.getPreferredUserInviteLanguage();
    },

    handleAddUser(event) {
      if (!validation.validateEmail(this.assigneeSearch)) {
        return;
      }
      this.assigneeOptions = [...this.assigneeOptions, this.assigneeSearch];
      this.assigneeUsername = this.assigneeOptions.at(-1);
      this.$refs.autocomplete.isMenuActive = false;

      // this.$refs.test.closeConditional();
      // this.$refs.test.blur();

      // event.target.closeConditional();
      // document.activeElement.blur();
    },

    loadAssignees(initalLoad = false) {
      let fetchAssignees = () => {
        return new Promise((resolve, reject) => {
          this.assigneeSearch = this.assigneeSearch ? this.assigneeSearch : "";

          if (
            this.dealersUuid.responsive_dealer === null &&
            this.dealersUuid.sales_dealer === null
          ) {
            return reject("Responsive and sales dealer are empty");
          }

          const uniqueDealerUuids = [
            ...new Set([
              this.dealersUuid.responsive_dealer,
              this.dealersUuid.sales_dealer,
            ]),
          ];

          const dealersUuids = uniqueDealerUuids.filter((item) => {
            return !!item;
          });

          if (dealersUuids == null || dealersUuids.length === 0) {
            return reject("Responsive or sales dealer is empty");
          }

          const promises = dealersUuids.map((item) => {
            return api.getDealer(item, {}, false);
          });

          Promise.allSettled(promises)
            .then((values) => {
              values = values.filter((item) => item.status === "fulfilled");

              if (values.length === 0) return;

              values = values.map((item) => {
                return item.value;
              });

              const users = values.flatMap((item) => item.data.users);

              this.assigneeOptions = [...this.assigneeOptions, ...users].filter(
                (item, index, array) =>
                  array.findIndex(
                    (element) => element.username === item.username
                  ) === index
              );

              const checkForAssignee = () => {
                return this.assigneeOptions
                  .map((assignee) => assignee.username)
                  .indexOf(values[0].data.email);
              };

              if (values[0].data.email !== null && checkForAssignee() !== -1) {
                // this.assigneeUsername = values[0].data.email;
                resolve();
              }

              if (values[0].data.email !== null && checkForAssignee() === -1) {
                this.assigneeOptions.push({
                  username: values[0].data.email,
                  uuid: this.assigneeOptions.length,
                });
                // this.assigneeUsername = values[0].data.email;
                resolve();
                // return;
              }

              if (this.assigneeOptions.length >= 2) {
                // this.assigneeUsername = this.assigneeOptions[1].username;
                resolve();
                // return;
              }
            })
            .catch((err) => {
              console.error(err);
            });
        });
      };

      if (initalLoad) return fetchAssignees();

      if (this.timeout) clearTimeout(this.timeout);

      this.timeout = setTimeout(() => {
        return fetchAssignees();
      }, 300); // delay
    },

    handleSave() {
      if (this.$refs.form.validate()) {
        if (this.dialog.activeUser === this.assigneeUsername) {
          snackbar.error("The user is already the owner of this boat");
          return;
        }

        const language = this.setPreferredUserInviteLanguage(
          this.inviteUserLanguage
        );
        this.$emit(
          "transfer",
          this.assigneeUsername,
          this.transferHistoricalData,
          this.inviteUserLanguage
        );
      }
    },
    handleCancel() {
      this.$emit("transfer", false);
    },
  },
};
</script>
<style scoped>
.v-card__text,
.v-card__title {
  word-break: normal;
}
</style>
